import z from "zod";
import type { FormProps } from ".";

export type Field = {
  type: React.HTMLInputTypeAttribute | "select" | "textarea" | "picker";
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean | number | string; // APIから 1/0 または true/false または "1"/"0" が来る可能性がある
  options?: {
    value: string;
  }[];
  // Picker specific properties
  start?: number;
  length?: number;
  calc?: "ADD" | "SUB";
  includeTime?: boolean; // true = YYYY MM DD hh ii, false = YYYY MM DD
};

export type Page = {
  fields: Field[];
};

export type FormSchema = {
  pages: Page[];
};

export function convertFormPropsToSchema(formProps: FormProps): FormSchema {
  return {
    pages: formProps.page
      .filter((page) => {
        // 空のページを除外: list がない、または list のすべての form_name が空
        if (!page.list || page.list.length === 0) return false;

        // すべての list アイテムに有効な fields があるかチェック
        const hasValidFields = page.list.some(
          (listItem) =>
            listItem.fields &&
            listItem.fields.length > 0 &&
            (listItem.form_name || listItem.fields.some(f => f.name))
        );

        return hasValidFields;
      })
      .map((page) => {
        return {
          fields: page.list.flatMap((listItem) => listItem.fields),
        };
      }),
  };
}

export function createZodSchemaForStep(fields: Field[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  fields.forEach((field) => {
    let validator: z.ZodTypeAny;

    // required フィールドの正規化: 1, true, "1" → true, 0, false, undefined → false
    const isRequired = !!(field.required === true || field.required === 1 || field.required === "1");

    if (field.type === "picker") {
      if (field.includeTime) {
        // YYYY MM DD hh ii 形式
        validator = z
          .object({
            year: z.string(),
            month: z.string(),
            day: z.string(),
            hour: z.string(),
            minute: z.string(),
          })
          .refine((val) => !!(val.year && val.month && val.day && val.hour && val.minute), {
            message: "日付と時間を全て選択してください",
          })
          .transform((val) => `${val.year}-${val.month}-${val.day} ${val.hour}:${val.minute}`);
      } else {
        // YYYY MM DD 形式
        validator = z
          .object({
            year: z.string(),
            month: z.string(),
            day: z.string(),
          })
          .refine((val) => !!(val.year && val.month && val.day), {
            message: "日付を全て選択してください",
          })
          .transform((val) => `${val.year}-${val.month}-${val.day}`);
      }
    } else if (field.type === "email") {
      // メールフィールド
      if (isRequired) {
        validator = z.string().min(1, "必須項目です").email("正しい形式で入力してください");
      } else {
        validator = z.string().email("正しい形式で入力してください").or(z.literal(""));
      }
    } else if (field.type === "tel") {
      // 電話番号フィールド
      if (isRequired) {
        validator = z.string().min(1, "必須項目です").regex(/^[0-9-]+$/, "正しい電話番号を入力してください");
      } else {
        validator = z.string().regex(/^[0-9-]*$/, "正しい電話番号を入力してください").or(z.literal(""));
      }
    } else if (field.type === "date") {
      // 日付フィールド (YYYY-MM-DD)
      if (isRequired) {
        validator = z.string().min(1, "必須項目です");
      } else {
        validator = z.string().optional();
      }
    } else if (field.type === "datetime-local") {
      // 日時フィールド (YYYY-MM-DDTHH:mm)
      if (isRequired) {
        validator = z.string().min(1, "必須項目です");
      } else {
        validator = z.string().optional();
      }
    } else {
      // その他のテキストフィールド
      if (isRequired) {
        validator = z.string().min(1, "必須項目です");
      } else {
        validator = z.string().optional();
      }
    }

    shape[field.name] = validator;
  });

  return z.object(shape);
}
